import { useState } from "react";
import { 
  Factory, 
  TreePine, 
  ClipboardCheck, 
  Stamp, 
  ArrowLeftRight, 
  Leaf,
  ArrowRight
} from "lucide-react";

const flowSteps = [
  {
    id: 1,
    title: "Emissions Generated",
    icon: Factory,
    description: "Industries and activities produce greenhouse gas emissions that contribute to climate change.",
    color: "text-slate-600",
    bgColor: "bg-slate-100"
  },
  {
    id: 2,
    title: "Project Development",
    icon: TreePine,
    description: "Climate projects are developed to reduce or remove emissions - reforestation, renewable energy, etc.",
    color: "text-forest-700",
    bgColor: "bg-forest-50"
  },
  {
    id: 3,
    title: "Verification (MRV)",
    icon: ClipboardCheck,
    description: "Independent verifiers validate emissions reductions through Monitoring, Reporting & Verification.",
    color: "text-earth-700",
    bgColor: "bg-earth-50"
  },
  {
    id: 4,
    title: "Credit Issuance",
    icon: Stamp,
    description: "Verified carbon credits are issued by recognized registries, each representing 1 tonne of CO₂.",
    color: "text-teal-600",
    bgColor: "bg-teal-50"
  },
  {
    id: 5,
    title: "Trading",
    icon: ArrowLeftRight,
    description: "Credits are bought and sold in carbon markets, connecting buyers with verified climate projects.",
    color: "text-amber-600",
    bgColor: "bg-amber-50"
  },
  {
    id: 6,
    title: "Offset",
    icon: Leaf,
    description: "Organizations retire credits to offset their emissions, achieving carbon neutrality goals.",
    color: "text-green-600",
    bgColor: "bg-green-50"
  }
];

export default function CarbonMarketExplainer() {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <section className="py-16 lg:py-24 bg-slate-50" data-testid="carbon-market-explainer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="font-montserrat text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            How Carbon Markets Work
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Understanding the journey from emissions to offsets through the carbon credit lifecycle.
          </p>
        </div>

        {/* Flow Steps - Desktop */}
        <div className="hidden lg:block">
          <div className="flex items-start justify-between relative">
            {/* Connecting Line */}
            <div className="absolute top-10 left-0 right-0 h-0.5 bg-slate-200" />
            
            {flowSteps.map((step, index) => (
              <div
                key={step.id}
                className="relative flex flex-col items-center w-1/6 px-2"
                onMouseEnter={() => setActiveStep(step.id)}
                onMouseLeave={() => setActiveStep(null)}
              >
                {/* Step Circle */}
                <div 
                  className={`flow-step relative z-10 w-20 h-20 rounded-full ${step.bgColor} flex items-center justify-center cursor-pointer transition-all duration-300 ${activeStep === step.id ? 'scale-110 shadow-lg' : ''}`}
                  data-testid={`flow-step-${step.id}`}
                >
                  <step.icon className={`h-8 w-8 ${step.color} flow-icon transition-colors duration-300`} />
                </div>

                {/* Arrow */}
                {index < flowSteps.length - 1 && (
                  <ArrowRight className="absolute top-8 -right-4 h-5 w-5 text-slate-300 z-0" />
                )}

                {/* Title */}
                <h3 className={`mt-4 text-sm font-semibold text-center ${step.color} transition-colors`}>
                  {step.title}
                </h3>

                {/* Hover Description */}
                {activeStep === step.id && (
                  <div className="absolute top-28 left-1/2 -translate-x-1/2 w-64 p-4 bg-white rounded-xl shadow-lg border border-slate-100 z-20 animate-fade-in">
                    <p className="text-sm text-slate-600 text-center">
                      {step.description}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Flow Steps - Mobile */}
        <div className="lg:hidden space-y-4">
          {flowSteps.map((step, index) => (
            <div
              key={step.id}
              className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100"
            >
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${step.bgColor} flex items-center justify-center`}>
                <step.icon className={`h-6 w-6 ${step.color}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-400">Step {step.id}</span>
                  {index < flowSteps.length - 1 && (
                    <ArrowRight className="h-3 w-3 text-slate-300" />
                  )}
                </div>
                <h3 className={`font-semibold ${step.color} mt-1`}>{step.title}</h3>
                <p className="text-sm text-slate-600 mt-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-white rounded-xl border border-slate-100">
            <p className="text-3xl font-bold text-forest-950">1 Credit</p>
            <p className="text-sm text-slate-600 mt-1">= 1 Tonne CO₂ Equivalent</p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl border border-slate-100">
            <p className="text-3xl font-bold text-earth-700">$100B+</p>
            <p className="text-sm text-slate-600 mt-1">Global Market Value (2025)</p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl border border-slate-100">
            <p className="text-3xl font-bold text-teal-600">50+</p>
            <p className="text-sm text-slate-600 mt-1">Countries with Carbon Markets</p>
          </div>
        </div>
      </div>
    </section>
  );
}
